<template>
    <div class="footer-link-page">
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && footerLink.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="footerLink"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                        />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :item="footerLink"
                            :edit-mode="editMode"
                            :sections="sections"
                            :options="options"
                            :campuses="footerLink.campuses"
                            :campuses-error="campusesError"
                            @on-delete-confirm="onDeleteConfirm"
                            @on-campuses-change="onCampusesChange"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';
import { required } from 'vuelidate/lib/validators';
import AddEditForm from '@/components/footer-links/AddEditForm';
import ItemInformation from '@/components/elements/ItemInformation';

export default {
    page: {
        title: 'Manage Footer Link'
    },

    validations: {
        footerLink: {
            text: {
                required
            },
            link: {
                required
            },
            columnId: {
                required
            },
            campuses: {
                atLeastOneItem: val => val?.length > 0
            },
            order: {
                required
            }
        }
    },

    components: {
        AddEditForm,
        ItemInformation
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            id,
            title: id ? 'Edit footer link' : 'Add footer link',
            editMode: !!id,
            footerLink: {
                campuses: [],
                order: null
            },
            serverErrors: [],
            options: {},
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Footer Links',
                    to: '/footer-links'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ]
        };
    },

    computed: {
        sections() {
            const sections = {
                campuses: true
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        },

        campusesError() {
            return this.$getValidationErrorMessageHandler.handle(
                this.$v.footerLink,
                this.serverErrors,
                'campuses'
            );
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.footerLink = await this.getFooterLink(this.id);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }

            return;
        }
    },

    methods: {
        ...mapActions({
            getFooterLink: 'footerLinks/show',
            addFooterLink: 'footerLinks/store',
            updateFooterLink: 'footerLinks/update',
            deleteFooterLink: 'footerLinks/destroy',
            getFooterLinks: 'footerLinks/index'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const campuses = this.footerLink.campuses.map(campus => campus.id);

            const data = {
                ...this.footerLink,
                campuses
            };

            try {
                if (!this.editMode) {
                    const added = await this.addFooterLink(data);

                    this.$router.push(`/footer-links/${added.id}/edit`);
                } else {
                    const { updatedAt } = await this.updateFooterLink({
                        id: this.id,
                        data
                    });

                    this.footerLink.updatedAt = updatedAt;

                    this.$toaster('Footer link updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data?.errors) {
                        this.serverErrors = data.errors;

                        console.log(this.serverErrors);

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteFooterLink(this.id);

                this.$router.push('/footer-links');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        onCampusesChange(campuses) {
            this.footerLink.campuses = campuses;
            this.informationKey++;
        }
    }
};
</script>
